import React, { useMemo, useState } from 'react';

import { useStreetsQuery } from '@/api/getters';
import ControlledComboBox from '@/components/ui/ComboBox/ControlledCombobox';
import { FormControl, FormField, FormItem } from '@/components/ui/Form';
import { Input } from '@/components/ui/Input';
import { validateAddress } from '@/features/v2/summaryForms/Validate';
import { EnumLike } from '@/types';

import { OWNER_CITY_NAME } from './InputCity';
import { useLingui } from '@lingui/react';
import debounce from 'lodash/debounce';
import { useFormContext } from 'react-hook-form';

export const OWNER_ADDRESS_NAME = 'owner.address';
export const OWNER_ADDRESS_COMPANY_NAME = 'owner.address_company';

const InputAddress = ({
  prefix,
  name = OWNER_ADDRESS_NAME,
  focused,
  setFocused,
  disabled,
  autoCompleteOff = false,
  controlled,
  creatable,
}: {
  prefix?: string;
  name: string;
  focused: string;
  setFocused: (value: string) => void;
  disabled?: boolean;
  autoCompleteOff?: boolean;
  controlled?: boolean;
  creatable?: boolean;
}) => {
  const { i18n } = useLingui();
  const { trigger, watch } = useFormContext();

  const [query, setQuery] = useState('');

  const city = watch(prefix ? `${prefix}.city` : OWNER_CITY_NAME);
  const withoutStreet = controlled && !!watch(`${prefix}.without_street`);

  const streets = useStreetsQuery(
    query,
    city?.value,
    !!query && query.length > 2
  );

  const triggerRefetch = () => {
    setTimeout(() => streets.refetch(), 10);
  };

  const changeHandler = (val: string) => {
    setQuery(val);
    if (val.length > 2 || val === '') {
      triggerRefetch();
    }
  };
  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, !!query && query.length > 4 ? 800 : 200),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query]
  );

  if (controlled) {
    return (
      <FormField
        name={name}
        disabled={disabled || withoutStreet}
        rules={{
          validate: (value: EnumLike) =>
            withoutStreet || validateAddress(value),
        }}
        render={({ field, fieldState }) => {
          const { onChange, onBlur, ...restField } = field;

          const setQueryAndClear = (val: string) => {
            if (val && field.value) {
              onChange(null);
            }
            debouncedChangeHandler(val);
          };

          return (
            <FormItem className="col-span-4">
              <FormControl>
                <ControlledComboBox
                  field={field}
                  options={
                    streets.data && streets.data?.length > 0
                      ? streets.data
                      : field?.value
                        ? [field.value]
                        : []
                  }
                  label={i18n.t('Adres')}
                  query={query}
                  setQuery={setQueryAndClear}
                  disabled={disabled}
                  error={fieldState.error?.message}
                  success={
                    focused !== field.name && !fieldState.error && field.value
                  }
                  onFocus={() => {
                    setFocused(field.name);
                    if (streets.data?.length === 0 && query === '') {
                      triggerRefetch();
                    }
                  }}
                  onChange={onChange}
                  onBlur={() => {
                    onBlur();
                    setFocused('');
                  }}
                  onFocusOut={() => {
                    trigger(field.name);
                    setFocused('');
                  }}
                  creatable={creatable}
                  {...restField}
                />
              </FormControl>
            </FormItem>
          );
        }}
      />
    );
  }

  return (
    <FormField
      name={name}
      rules={{
        validate: (value: string) => validateAddress({ label: value, value }),
      }}
      render={({ field, fieldState }) => {
        const { onChange, onBlur, ...restField } = field;
        return (
          <FormItem className="col-span-4">
            <FormControl>
              <Input
                label={i18n.t('Adres')}
                autoComplete={autoCompleteOff ? 'none' : 'street-address'}
                disabled={disabled}
                error={fieldState.error?.message}
                success={
                  focused !== field.name && !fieldState.error && field.value
                }
                onChange={(e) => {
                  onChange(e);
                  if (fieldState.error) {
                    trigger(field.name);
                  }
                }}
                onBlur={() => {
                  setFocused('');
                  onBlur();
                }}
                onFocus={() => setFocused(field.name)}
                {...restField}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};

export default InputAddress;
